import { useTranslation } from "react-i18next";
import { Check, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { PERSONA_QUESTS, type PersonaQuestId } from "../lib/completeness";
import { usePersonaSetup } from "../hooks/use-persona-setup";
import { ProgressRing } from "./ProgressRing";

export const QuestRail: React.FC = () => {
  const { t } = useTranslation();
  const { activeQuest, setActiveQuest, completeness } = usePersonaSetup();

  return (
    <aside className="flex flex-col gap-3 md:gap-5">
      <div className="flex items-center gap-3 md:gap-4">
        <ProgressRing
          percent={completeness.percent}
          size={64}
          className="md:hidden"
        />
        <ProgressRing
          percent={completeness.percent}
          size={88}
          className="hidden md:block"
        />
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-text-brand dark:text-indigo-400 md:text-xs">
            {t("personaSetup.badge")}
          </p>
          <p className="text-sm font-medium text-text-secondary dark:text-gray-300">
            {t("personaSetup.questsDone", {
              done: completeness.completedCount,
              total: completeness.total,
            })}
          </p>
        </div>
      </div>

      <ol className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [-ms-overflow-style:none] snap-x snap-mandatory md:mx-0 md:flex-col md:overflow-visible md:px-0 md:snap-none [&::-webkit-scrollbar]:hidden">
        {PERSONA_QUESTS.map((id, index) => {
          const done = completeness.quests[id];
          const active = activeQuest === id;
          const locked = !done && completeness.firstIncomplete !== id && !active;

          return (
            <li key={id} className="snap-start shrink-0 md:min-w-0 md:w-full">
              <button
                type="button"
                disabled={locked}
                onClick={() => setActiveQuest(id as PersonaQuestId)}
                className={cn(
                  "flex items-center gap-2 rounded-2xl border px-3 py-2 text-left transition-all md:w-full md:gap-3 md:py-2.5",
                  active
                    ? "border-text-brand/60 bg-indigo-50/80 shadow-sm dark:border-indigo-400/50 dark:bg-indigo-500/15"
                    : "border-border-secondary bg-white/80 dark:border-gray-600 dark:bg-gray-800/80",
                  locked && "cursor-not-allowed opacity-70"
                )}
              >
                <span
                  className={cn(
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold md:h-8 md:w-8",
                    done
                      ? "bg-emerald-500 text-white"
                      : active
                        ? "bg-text-brand text-white dark:bg-indigo-500"
                        : "bg-gray-100 text-text-tertiary dark:bg-gray-700 dark:text-gray-400"
                  )}
                >
                  {done ? (
                    <Check className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  ) : locked ? (
                    <Lock className="h-3 w-3 md:h-3.5 md:w-3.5" />
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="whitespace-nowrap text-sm font-semibold text-text-primary dark:text-gray-100 md:block md:truncate">
                  {t(`personaSetup.quests.${id}.rail`)}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </aside>
  );
};
